import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { cn } from '@/lib/utils.js';

/** Fixed export width so large DPR canvases do not bloat form_submissions JSON. */
export const SIGNATURE_EXPORT_MAX_WIDTH = 600;

export type SignaturePadHandle = {
  clear: () => void;
  /** True when at least one stroke was drawn (not a blank PNG). */
  hasInk: () => boolean;
  /** Base64 PNG data URL scaled to max width, or null if empty. */
  toDataUrl: () => string | null;
};

type SignaturePadProps = {
  className?: string;
  canvasClassName?: string;
  /** Visual height of the drawing surface (CSS px). */
  height?: number;
  disabled?: boolean;
  /** Fires when ink becomes present or is cleared. */
  onInkChange?: (hasInk: boolean) => void;
  /** Called after each stroke with export URL, or null when cleared. */
  onChange?: (dataUrl: string | null) => void;
  showClear?: boolean;
  clearLabel?: string;
  helpText?: string;
  padRef?: MutableRefObject<SignaturePadHandle | null>;
};

function resizeCanvasBacking(canvas: HTMLCanvasElement, cssHeight: number): void {
  const parent = canvas.parentElement;
  const cssWidth = Math.max(parent?.clientWidth ?? canvas.clientWidth ?? 300, 100);
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const nextW = Math.floor(cssWidth * dpr);
  const nextH = Math.floor(cssHeight * dpr);
  if (canvas.width === nextW && canvas.height === nextH) return;

  // Preserve strokes when possible by copying bitmap before resize
  let prev: HTMLCanvasElement | null = null;
  if (canvas.width > 0 && canvas.height > 0) {
    prev = document.createElement('canvas');
    prev.width = canvas.width;
    prev.height = canvas.height;
    const pctx = prev.getContext('2d');
    if (pctx) pctx.drawImage(canvas, 0, 0);
  }

  canvas.width = nextW;
  canvas.height = nextH;
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${cssHeight}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  if (prev) {
    ctx.drawImage(prev, 0, 0, canvas.width, canvas.height);
  }
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 2.5 * dpr;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
}

/**
 * Export PNG scaled to maxCssWidth, matching waiver storage (base64 data URL).
 */
export function exportSignatureDataUrl(
  source: HTMLCanvasElement,
  maxCssWidth = SIGNATURE_EXPORT_MAX_WIDTH,
): string {
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const cssW = source.width / dpr;
  const cssH = source.height / dpr;
  const scale = cssW > maxCssWidth ? maxCssWidth / cssW : 1;
  const outW = Math.max(1, Math.round(cssW * scale));
  const outH = Math.max(1, Math.round(cssH * scale));
  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const ctx = out.getContext('2d');
  if (!ctx) return source.toDataURL('image/png');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(source, 0, 0, outW, outH);
  return out.toDataURL('image/png');
}

/**
 * Drawable signature canvas — mouse, touch, and stylus.
 * Reuses the waiver/customer-display pattern (strokes + hasInk flag).
 */
export function SignaturePad({
  className,
  canvasClassName,
  height = 160,
  disabled,
  onInkChange,
  onChange,
  showClear = true,
  clearLabel = 'Clear',
  helpText = 'Sign above',
  padRef,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const setInk = useCallback(
    (next: boolean) => {
      hasInkRef.current = next;
      setHasInk(next);
      onInkChange?.(next);
    },
    [onInkChange],
  );

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.5 * dpr;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setInk(false);
    onChange?.(null);
  }, [onChange, setInk]);

  const toDataUrl = useCallback((): string | null => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInkRef.current) return null;
    try {
      return exportSignatureDataUrl(canvas);
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!padRef) return;
    padRef.current = {
      clear,
      hasInk: () => hasInkRef.current,
      toDataUrl,
    };
    return () => {
      padRef.current = null;
    };
  }, [padRef, clear, toDataUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    resizeCanvasBacking(canvas, height);
    clear();

    const ro = new ResizeObserver(() => {
      const hadInk = hasInkRef.current;
      const prevUrl = hadInk ? exportSignatureDataUrl(canvas) : null;
      resizeCanvasBacking(canvas, height);
      if (prevUrl) {
        const img = new Image();
        img.onload = () => {
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          setInk(true);
        };
        img.src = prevUrl;
      }
    });
    const parent = canvas.parentElement;
    if (parent) ro.observe(parent);
    const onOrient = () => resizeCanvasBacking(canvas, height);
    window.addEventListener('orientationchange', onOrient);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', onOrient);
    };
    // clear only on mount sizing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  const posFromEvent = useCallback((e: PointerEvent | React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || disabled) return;

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      const p = posFromEvent(e);
      const ctx = canvas.getContext('2d');
      if (!ctx || !p) return;
      drawing.current = true;
      last.current = p;
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 2.5 * dpr;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    const onMove = (e: PointerEvent) => {
      if (!drawing.current) return;
      e.preventDefault();
      const p = posFromEvent(e);
      const ctx = canvas.getContext('2d');
      if (!ctx || !p || !last.current) return;
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last.current = p;
      if (!hasInkRef.current) {
        setInk(true);
      }
    };

    const onUp = (e: PointerEvent) => {
      if (!drawing.current) return;
      drawing.current = false;
      last.current = null;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      if (hasInkRef.current) {
        onChange?.(toDataUrl());
      }
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);

    const blockTouch = (ev: TouchEvent) => {
      ev.preventDefault();
    };
    canvas.addEventListener('touchstart', blockTouch, { passive: false });
    canvas.addEventListener('touchmove', blockTouch, { passive: false });

    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('touchstart', blockTouch);
      canvas.removeEventListener('touchmove', blockTouch);
    };
  }, [disabled, onChange, posFromEvent, setInk, toDataUrl]);

  return (
    <div className={cn('w-full', className)}>
      <div className="w-full overflow-hidden rounded-xl border-2 border-slate-300 bg-white">
        <canvas
          ref={canvasRef}
          className={cn(
            'block w-full touch-none bg-white',
            disabled && 'pointer-events-none opacity-60',
            canvasClassName,
          )}
          style={{ height, touchAction: 'none' }}
          aria-label="Signature pad"
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        {showClear ? (
          <button
            type="button"
            className="text-sm font-semibold text-primary disabled:opacity-40"
            disabled={disabled || !hasInk}
            onClick={clear}
          >
            {clearLabel}
          </button>
        ) : (
          <span />
        )}
        {helpText ? <span className="text-xs text-slate-500">{helpText}</span> : null}
      </div>
    </div>
  );
}
